'use client'

import { useMotionTemplate, useMotionValue, motion } from 'motion/react'
import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

// Keeps only the 1px padding ring of the element. Set inline, shorthand first: the
// `mask` shorthand resets `mask-composite` if it lands after it in a stylesheet.
const RING_MASK = {
  WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
  WebkitMaskComposite: 'xor',
  mask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
  maskComposite: 'exclude',
} as const

/**
 * Card whose border lights up in sage where the pointer is. It reacts to the
 * visitor rather than pulsing on its own, and does nothing on touch screens.
 */
export function SpotlightCard({ children, className }: { children: ReactNode; className?: string }) {
  const x = useMotionValue(-400)
  const y = useMotionValue(-400)
  const glow = useMotionTemplate`radial-gradient(320px circle at ${x}px ${y}px, color-mix(in oklab, var(--color-primary) 70%, transparent), transparent 70%)`

  return (
    <div
      className={cn('group relative isolate overflow-hidden rounded-2xl border border-border bg-card', className)}
      onPointerMove={(e) => {
        if (e.pointerType !== 'mouse') return
        const r = e.currentTarget.getBoundingClientRect()
        x.set(e.clientX - r.left)
        y.set(e.clientY - r.top)
      }}
    >
      {children}
      {/* The lit border: a 1px ring masked out of the same gradient, drawn above the content */}
      <motion.div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-30 rounded-[inherit] p-px opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        style={{ background: glow, ...RING_MASK }}
      />
    </div>
  )
}
